// src/components/Tableros/Tablas/SpeedResultTable.js
import React, { useState, useEffect } from 'react';
import { BsArrowUp, BsArrowDown } from 'react-icons/bs';
import io from 'socket.io-client';
import { useSearchParams } from 'react-router-dom';
import './SpeedResultTable.css';

const parseLapsRemaining = (lapsString) => {
  if (typeof lapsString !== 'string') return parseFloat(lapsString);
  const fractions = lapsString.split(' ');
  const fullLaps = parseFloat(fractions[0]);
  const fraction = fractions[1] ? eval(fractions[1].replace('/', '* 1.0 /')) : 0;
  return fullLaps + fraction;
};

const getPositionIndicator = (prevPos, currPos) => {
  const positionChange = prevPos - currPos;
  if (positionChange > 0) {
    return (
      <span className="change-position">
        <BsArrowUp /> +{positionChange}
      </span>
    );
  } else if (positionChange < 0) {
    return (
      <span className="change-position down">
        <BsArrowDown /> {positionChange}
      </span>
    );
  }
  return null;
};

const SpeedResultTable = (props) => {
  // Comprueba si estamos renderizando como página independiente o como componente embebido
  const [searchParams] = useSearchParams();
  const configId = searchParams.get('id');
  const isStandalone = !!configId;
  
  // Estado para el socket cuando estamos en modo independiente
  const [socketInstance, setSocketInstance] = useState(null);
  
  // Estados para los datos recibidos por socket
  const [resultsFromSocket, setResultsFromSocket] = useState([]);
  const [eventDetailsFromSocket, setEventDetailsFromSocket] = useState({
    eventName: "No hay eventos seleccionados",
    event: '',
    round: '',
    heat: '',
    wind: '',
    participants: 0,
  });
  const [clockFromSocket, setClockFromSocket] = useState('0.0');
  
  // Estados para configuración cuando estamos en modo independiente
  const [loadedConfig, setLoadedConfig] = useState(null);
  
  // Usa props directamente o valores cargados de localStorage
  const results = props.results || resultsFromSocket;
  const eventName = props.eventName || eventDetailsFromSocket;
  const clock = props.clock || clockFromSocket;
  
  // Aplicar configuración de props o del localStorage
  const config = isStandalone ? loadedConfig?.settings || {} : props;
  
  // Extraer valores de configuración
  const {
    showLane = false,
    showNumber = false,
    showLastLapTime = false,
    showLapsToGo = false,
    showLapsColumns = false,
    displayRows = 8,
    showTimeDifference = false,
    showReactionTime = false,
    tableWidth = 600,
    tableHeight = 800,
    tableBackgroundColor = '#1a1a1a',
    globalFontColor = '#ffffff',
    timerColor = '#00ff00',
    headerFontSize = 24,
    headerFontColor = '#ffffff',
    nameFontSize = 18,
    positionFontSize = 20,
    timeFontSize = 18,
    stopwatchSize = 50,
    largeStopwatchSize = 120,
    // Colores
    athleteNameColor = '#ffffff',
    teamNameColor = '#9e9c9c',
    resultTimeColor = '#ffffff',
    deltaTimeColor = '#c1ff30',
    positionColor = '#ffffff',
    tableRowOddColor = '#252525',
    tableRowEvenColor = '#1e1e1e',
    // Tipografías
    headerFontFamily = 'Roboto',
    nameFontFamily = 'Roboto',
    numbersFontFamily = 'Roboto Mono',
  } = config;
  
  // Resto de estados del componente original
  const [previousPositions, setPreviousPositions] = useState({});
  const [currentPositions, setCurrentPositions] = useState([]);
  const [lapsData, setLapsData] = useState(() => {
    const savedData = localStorage.getItem('lapsData');
    return savedData ? JSON.parse(savedData) : {};
  });
  const [totalLaps, setTotalLaps] = useState(0);
  const [mostrarCronometroGrande, setMostrarCronometroGrande] = useState(false);
  const [cronometroIniciado, setCronometroIniciado] = useState(false);

  // Cargar configuración desde localStorage si estamos en modo independiente
  useEffect(() => {
    if (isStandalone && configId) {
      try {
        const savedConfig = localStorage.getItem(`config-${configId}`);
        if (savedConfig) {
          console.log('Configuración cargada:', JSON.parse(savedConfig));
          setLoadedConfig(JSON.parse(savedConfig));
        } else {
          console.error('No se encontró configuración para el ID:', configId);
        }
      } catch (error) {
        console.error('Error al cargar la configuración:', error);
      }
    }
  }, [isStandalone, configId]);

  // Inicializar socket.io si estamos en modo independiente
  useEffect(() => {
    if (isStandalone && !socketInstance) {
      const socket = io.connect('http://localhost:8000');
      
      socket.on('connect', () => {
        console.log('Tablero conectado al servidor socket.io');
      });
      
      socket.on('disconnect', () => {
        console.log('Tablero desconectado del servidor socket.io');
      });
      
      setSocketInstance(socket);
      
      return () => {
        socket.disconnect();
      };
    }
  }, [isStandalone, socketInstance]);

  // Procesamiento de mensajes de socket
  useEffect(() => {
    if (isStandalone && socketInstance) {
      socketInstance.on('message', (message) => {
        try {
          const obj = JSON.parse(message);
          if (obj.length < 2) return;
          
          // Procesar los detalles del evento
          if (obj[1] && obj[1].length >= 6) {
            const newEventDetails = {
              eventName: obj[1][1] || 'No hay eventos seleccionados',
              wind: obj[1][2] || 'NM',
              event: obj[1][3] || '0',
              round: obj[1][4] || '0',
              heat: obj[1][5] || '0',
              participants: obj[1][8] || '0',
            };
            setEventDetailsFromSocket(newEventDetails);
            
            if (newEventDetails.eventName === 'No hay eventos seleccionados') {
              // Resetear datos
              setResultsFromSocket([]);
              setClockFromSocket('0.0');
            }
          }
          
          // Procesar el reloj
          if (obj[0]) {
            const formattedClock = formatTime(obj[0][0]);
            setClockFromSocket(formattedClock);
          }
          
          // Procesar los resultados
          if (obj.length > 2) {
            const newResults = [];
            for (let i = 2; i < obj.length; i++) {
              newResults.push(obj[i]);
            }
            setResultsFromSocket(newResults);
          }
        } catch (error) {
          console.error('Error procesando mensaje:', error);
        }
      });
      
      socketInstance.on('clearBoard', () => {
        setResultsFromSocket([]);
        setClockFromSocket('0.0');
      });
      
      return () => {
        socketInstance.off('message');
        socketInstance.off('clearBoard');
      };
    }
  }, [isStandalone, socketInstance]);

  // Función para eliminar los decimales del tiempo
  const formatTime = (timeString) => {
    if (!timeString) return '0';
    return timeString.split('.')[0];
  };

  // Escuchar eventos de window.postMessage para actualización en tiempo real
  useEffect(() => {
    if (isStandalone) {
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'updateConfig') {
          console.log('Recibida solicitud de actualización');
          try {
            const savedConfig = localStorage.getItem(`config-${configId}`);
            if (savedConfig) {
              console.log('Configuración actualizada:', JSON.parse(savedConfig));
              setLoadedConfig(JSON.parse(savedConfig));
            }
          } catch (error) {
            console.error('Error al actualizar la configuración:', error);
          }
        }
      };
      
      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [isStandalone, configId]);

  // Comprobar si eventName cambia
  useEffect(() => {
    const savedEvent = localStorage.getItem('eventName');
    if (eventName.eventName === 'No hay eventos seleccionados') {
      localStorage.removeItem('lapsData'); // Solo removemos lapsData, no todo localStorage
      setLapsData({});
      setCurrentPositions([]);
    } else if (savedEvent !== eventName.eventName) {
      localStorage.setItem('eventName', eventName.eventName);
      setLapsData({});
    }
  }, [eventName]);

  // Efectos para mostrar el cronómetro grande/pequeño
  useEffect(() => {
    if (cronometroIniciado && results.every(result => result[0] === '' && result[5] === '')) {
      setMostrarCronometroGrande(true);
    } else {
      setMostrarCronometroGrande(false);
    }
  
    if (results.length > 0) {
      const validResults = results.filter(result => result[0] !== "DNS");
  
      const sortedResults = [...validResults].sort((a, b) => {
        const lapsA = parseLapsRemaining(a[9]);
        const lapsB = parseLapsRemaining(b[9]);
  
        if (isNaN(lapsA) || isNaN(lapsB)) return 0;
  
        return lapsA - lapsB;
      });
  
      const maxLaps = Math.ceil(Math.max(...results.map(result => {
        const laps = parseLapsRemaining(result[9]);
        return isNaN(laps) ? 0 : laps;
      })));
      setTotalLaps(maxLaps > 0 ? maxLaps : 0);
  
      // Copia de lapsData para evitar mutación directa
      const newLapsData = JSON.parse(JSON.stringify(lapsData));
      
      const tempPositions = sortedResults.map((result, index) => {
        const lapsRemaining = parseLapsRemaining(result[9]);
        const cumulativeTime = result[7];
        const number = result[2];
  
        if (!newLapsData[number]) {
          newLapsData[number] = { positions: [], times: [], positionChanges: [] };
        }
  
        if (lapsRemaining < maxLaps && !isNaN(lapsRemaining)) {
          const currentLap = maxLaps - lapsRemaining;
  
          if (currentLap >= 0 && currentLap <= maxLaps && !newLapsData[number].positions[currentLap - 1]) {
            newLapsData[number].positions[currentLap - 1] = index + 1;
            newLapsData[number].times[currentLap - 1] = cumulativeTime;
  
            const prevPos = newLapsData[number].positions[currentLap - 2] || index + 1;
            newLapsData[number].positionChanges[currentLap - 1] = getPositionIndicator(prevPos, index + 1);
          }
        }
  
        return {
          number: result[2],
          lapsRemaining: result[9],
          currentPosition: result[0],
          name: result[3],
          team: result[4],
          lane: result[1],
          deltaTime: result[6],
          lastLapTime: result[8],
          reactionTime: result[11],
        };
      });
  
      setCurrentPositions(tempPositions);
      
      // Actualizar lapsData de forma inmutable
      setLapsData(newLapsData);
      localStorage.setItem('lapsData', JSON.stringify(newLapsData));
  
      const newPreviousPositions = {};
      tempPositions.forEach(pos => {
        newPreviousPositions[pos.number] = pos.currentPosition;
      });
  
      setPreviousPositions(newPreviousPositions);
    }
  }, [results, cronometroIniciado]); // Remover lapsData de las dependencias

  useEffect(() => {
    if (clock !== "0") {
      setCronometroIniciado(true);
    } else {
      setCronometroIniciado(false);
    }
  }, [clock]);

  const getDynamicFontSize = () => {
    if (mostrarCronometroGrande) {
      // Usar el tamaño del cronómetro grande para el cronómetro en modo grande
      const baseFontSize = largeStopwatchSize;
      const rowsFactor = Math.max(1, displayRows);
      const heightFactor = tableHeight / rowsFactor;
      const dynamicFontSize = Math.min(baseFontSize, heightFactor * 0.8);
      return `${dynamicFontSize}px`;
    }
    // Usar el tamaño del cronómetro pequeño para el encabezado
    return `${stopwatchSize}px`;
  };
  
  // Si estamos en modo independiente y aún no hemos cargado la configuración, mostramos un mensaje de carga
  if (isStandalone && !loadedConfig) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: '#1a1a1a', 
        color: '#ffffff',
        fontFamily: headerFontFamily
      }}>
        <h2>Cargando configuración del tablero...</h2>
      </div>
    );
  }

  const hasValidResults = () => {
    return results.some(result => 
      result[0] !== '' && 
      result[0] !== undefined && 
      result[0] !== null && 
      result[0] !== 'DNS'
    );
  };

  const updateLapsData = (updates) => {
    setLapsData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));
      Object.entries(updates).forEach(([number, data]) => {
        if (!newData[number]) {
          newData[number] = { positions: [], times: [], positionChanges: [] };
        }
        
        if (data.positions) {
          newData[number].positions = [...data.positions];
        }
        
        if (data.times) {
          newData[number].times = [...data.times];
        }
        
        if (data.positionChanges) {
          // Para positionChanges, necesitamos manejar los elementos React
          newData[number].positionChanges = [...data.positionChanges];
        }
      });
      
      // Guardar en localStorage fuera del ciclo de renderizado
      localStorage.setItem('lapsData', JSON.stringify(newData));
      return newData;
    });
  };

  return (
    <div
      className="scoreboard-container"
      style={{
        width: `${tableWidth}px`,
        height: `${tableHeight}px`,
        backgroundColor: tableBackgroundColor,
        color: globalFontColor,
        padding: '20px',
        overflowY: 'auto',
        fontFamily: headerFontFamily
      }}
    >
      <div
        className="table-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
      <div
        style={{
          backgroundColor: 'transparent',
          color: headerFontColor || '#ffffff',
          fontSize: `${headerFontSize}px`,
          fontFamily: headerFontFamily,
          padding: '10px',
          borderRadius: '5px',
          flexGrow: 1,
          textAlign: 'center',
        }}
        className="event-name"
      >
        {eventName.eventName || 'No hay eventos seleccionados'}
      </div>
        {!mostrarCronometroGrande && (
          <div
            style={{
              color: timerColor,
              fontSize: getDynamicFontSize(),
              fontFamily: numbersFontFamily,
              marginLeft: '20px',
            }}
            className="cronometro-pequeno"
          >
            {clock}
          </div>
        )}
      </div>
      {mostrarCronometroGrande ? (
        <div
          className="cronometro-grande"
          style={{
            color: timerColor,
            fontSize: getDynamicFontSize(),
            fontFamily: numbersFontFamily,
            textAlign: 'center',
            margin: '20px 0',
          }}
        >
          {clock}
        </div>
      ) : (
        <div
          id="results"
          className="table-responsive"
          style={{ height: `calc(${tableHeight}px - 100px)`, overflowY: 'auto' }}
        >
          <table
            className="table table-striped table-hover animate__animated animate__fadeIn"
            style={{ width: '100%', backgroundColor: tableBackgroundColor, color: globalFontColor }}
          >
            <thead
              className="table-dark"
              style={{
                backgroundColor: tableBackgroundColor,
                color: headerFontColor || '#ffffff',
                fontSize: '16px',
                fontFamily: headerFontFamily
              }}
            >
        <tr>
          {/* Mostrar columna "Lugar" solo si hay resultados válidos */}
          {hasValidResults() && <th className="place">Lugar</th>}
          
          {(!hasValidResults() || showLane) && <th className="lane">Carril</th>}
          {showNumber && <th className="number">#</th>}
          <th className="name">Nombre</th>
          
          {/* Mostrar estas columnas solo si hay resultados y son habilitadas */}
          {hasValidResults() && showLastLapTime && <th className="lastLapTime">T. Última Vuelta</th>}
          {hasValidResults() && !showLapsColumns && <th className="time">Tiempo</th>}
          {hasValidResults() && showLapsColumns && [...Array(totalLaps)].map((_, i) => (
            <th key={`lap-${i + 1}`}>{i + 1 === totalLaps ? "Meta" : `Vuelta ${i + 1}`}</th>
          ))}
          {hasValidResults() && showReactionTime && <th className="reactionTime">Reaction</th>}
          {hasValidResults() && showLapsToGo && <th className="lapsToGo">TO GO</th>}
        </tr>
      </thead>
      <tbody>
        {currentPositions.slice(0, displayRows).map((pos, index) => (
          <tr
            key={pos.number}
            style={{
              backgroundColor: index % 2 === 0 ? tableRowOddColor : tableRowEvenColor,
              color: globalFontColor,
            }}
          >
            {/* Mostrar columna "Lugar" solo si hay resultados válidos */}
            {hasValidResults() && (
              <td className="current-Position" style={{ 
                fontSize: `${positionFontSize}px`,
                fontFamily: numbersFontFamily,
                color: positionColor || globalFontColor
              }}>
                {pos.currentPosition}
              </td>
            )}
            
            {(!hasValidResults() || showLane) && (
              <td style={{ 
                fontSize: `${timeFontSize}px`,
                fontFamily: numbersFontFamily
              }}>
                {pos.lane || ''}
              </td>
            )}
            
            {showNumber && (
              <td style={{ 
                fontSize: `${timeFontSize}px`,
                fontFamily: numbersFontFamily 
              }}>
                {pos.number || ''}
              </td>
            )}
            
            <td>
              <div className="athlete-name" style={{ 
                fontSize: `${nameFontSize}px`,
                fontFamily: nameFontFamily,
                color: athleteNameColor || globalFontColor
              }}>
                {pos.name || ''}
              </div>
              <div className="team-name" style={{ 
                fontSize: `${nameFontSize * 0.8}px`,
                fontFamily: nameFontFamily,
                color: teamNameColor || globalFontColor
              }}>
                {pos.team || ''}
              </div>
            </td>
            
            {/* Mostrar estas columnas solo si hay resultados y son habilitadas */}
            {hasValidResults() && showLastLapTime && (
              <td style={{ 
                fontSize: `${timeFontSize}px`,
                fontFamily: numbersFontFamily
              }}>
                {pos.lastLapTime || ''}
              </td>
            )}
            
            {hasValidResults() && !showLapsColumns && (
              <td>
                <div className="result-time" style={{ 
                  fontSize: `${timeFontSize}px`,
                  fontFamily: numbersFontFamily,
                  color: resultTimeColor || globalFontColor
                }}>
                  {results[index]?.[5] || ''}
                </div>
                {showTimeDifference && 
                  <div className="delta-time" style={{
                    color: deltaTimeColor || globalFontColor,
                    fontFamily: numbersFontFamily
                  }}>
                    {pos.deltaTime || ''}
                  </div>
                }
              </td>
            )}
            
            {hasValidResults() && showLapsColumns && [...Array(totalLaps)].map((_, i) => (
              <td key={`lap-${i}-${pos.number}`}>
                {lapsData[pos.number]?.times[i] ? (
                  <>
                    <div style={{ 
                      color: resultTimeColor || globalFontColor,
                      fontFamily: numbersFontFamily
                    }}>
                      {lapsData[pos.number]?.times[i]}
                    </div>
                    <div style={{
                      fontFamily: numbersFontFamily
                    }}>
                      #{lapsData[pos.number]?.positions[i]}{' '}
                      {lapsData[pos.number]?.positionChanges[i] || null}
                    </div>
                  </>
                ) : (
                  ''
                )}
              </td>
            ))}
            
            {hasValidResults() && showReactionTime && (
              <td style={{ 
                fontSize: `${timeFontSize}px`,
                fontFamily: numbersFontFamily
              }}>
                {pos.reactionTime || ''}
              </td>
            )}
            
            {hasValidResults() && showLapsToGo && (
              <td style={{ 
                fontSize: `${timeFontSize}px`,
                fontFamily: numbersFontFamily
              }}>
                {pos.lapsRemaining || ''}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}
      <div style={{ 
        padding: '5px', 
        textAlign: 'right',
        fontSize: '10px',
        color: headerFontColor,
        opacity: 0.6,
        fontFamily: headerFontFamily
      }}>
        Athletic LIVE
      </div>
    </div>
  );
};

export default SpeedResultTable;